import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gem, BadgeCheck, PlayCircle, Loader2, CheckCircle2, XCircle, ToggleRight, ToggleLeft } from "lucide-react";

const STREAMS = [
  ["resource_intel", "Resource Intelligence"], ["exploration_knowledge", "Exploration Knowledge"],
  ["core_repository", "Digital Core"], ["mining_twin", "Mining Digital Twin"],
  ["supply_chain", "Supply Chain & Provenance"], ["critical_minerals", "Critical Minerals"],
  ["beneficiation", "Beneficiation"], ["illegal_mining", "Illegal Mining Intel"],
  ["water_energy_carbon", "Water · Energy · Carbon"], ["economics_fiscal", "Economics & Fiscal"],
  ["geopolitical", "Geopolitical"], ["innovation", "Innovation & Research"],
  ["autonomous", "Autonomous Mining"], ["emergency", "Emergency & Crisis"],
  ["policy", "Policy & Strategy"], ["passport", "Mineral Passport"],
  ["global_intel", "Global Mineral Intel"], ["knowledge_graph", "Knowledge Graph"],
];

const KV = ({ k, v }) => (
  <div className="border-b cad-hairline pb-1 flex items-center gap-2 text-[12px]">
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5] flex-1 truncate">{k}</div>
    <div className="font-mono-cad text-[#F6F8FA] break-all text-right max-w-[65%]">{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
  </div>
);

const StreamPanel = ({ k }) => {
  const q = useQuery({ queryKey: ["mp-" + k], queryFn: async () => (await api.get("/cadastre360/mineral-plus/" + k)).data });
  if (q.isLoading) return <div className="cad-panel p-6 text-[#B3C2CC]">Loading…</div>;
  if (q.isError) return <div className="cad-panel p-6 text-[#E67E7E]">Stream unavailable</div>;
  const d = q.data || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {Object.entries(d).map(([key, val]) => {
        if (key === "generated_at") return null;
        if (typeof val === "object" && val !== null) {
          return (
            <div key={key} className="cad-panel p-4">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">{key.replace(/_/g, " ")}</div>
              <div className="mt-2 space-y-1 max-h-[320px] overflow-y-auto">
                {(Array.isArray(val) ? val.slice(0, 30) : Object.entries(val).slice(0, 24)).map((entry, i) => {
                  if (Array.isArray(val)) {
                    return <div key={i} className="text-[11px] text-[#DCE3E8] border-b cad-hairline pb-1">{typeof entry === "object" ? JSON.stringify(entry) : String(entry)}</div>;
                  }
                  const [kk, vv] = entry;
                  return <KV key={kk} k={kk} v={vv} />;
                })}
              </div>
            </div>
          );
        }
        return (
          <div key={key} className="cad-panel px-3 py-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{key.replace(/_/g, " ")}</div>
            <div className="font-display font-bold text-[#F6F8FA] mt-0.5">{typeof val === "boolean" ? (val ? "yes" : "no") : String(val)}</div>
          </div>
        );
      })}
    </div>
  );
};

const AcceptancePanel = () => {
  const qc = useQueryClient();
  const flags = useQuery({ queryKey: ["mp-flags"], queryFn: async () => (await api.get("/cadastre360/mineral-plus/feature-flags")).data });
  const reports = useQuery({ queryKey: ["mp-acc"], queryFn: async () => (await api.get("/cadastre360/mineral-plus/acceptance/reports")).data });
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/mineral-plus/acceptance/run")).data,
    onSuccess: (d) => { toast.success("Phase VI " + d.overall + " · " + d.passed + "/" + d.scope_size); qc.invalidateQueries({ queryKey: ["mp-acc"] }); },
  });
  const toggle = useMutation({
    mutationFn: async ({ name, enabled }) => (await api.post("/cadastre360/mineral-plus/feature-flags/" + name, { enabled })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mp-flags"] }); },
  });
  const latest = (reports.data || [])[0];
  return (
    <div className="space-y-3">
      <div className="cad-panel p-4 flex flex-wrap items-center gap-3">
        <div className="text-[12px] text-[#B3C2CC] flex-1 min-w-[220px]">Run Phase VI sovereign mineral governance acceptance across all 18 streams.</div>
        <button onClick={() => run.mutate()} disabled={run.isPending} className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="mp-run">
          {run.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />} Run acceptance
        </button>
      </div>
      {latest && (
        <div className="cad-panel p-4">
          <div className="flex items-center gap-3">
            <span className={"cad-chip text-[10px] uppercase " + (latest.overall === "PASS" ? "cad-chip-green" : "cad-chip-red")}>{latest.overall}</span>
            <span className="font-mono-cad text-[11px] text-[#F6F8FA]">{latest.passed}/{latest.scope_size}</span>
            <span className="font-mono-cad text-[10px] text-[#7C93A5] ml-auto">{latest.run_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1">
            {(latest.checks || []).map((c, i) => (
              <div key={i} className="border-b cad-hairline pb-1 flex items-start gap-2">
                {c.ok ? <CheckCircle2 size={12} className="text-[#6BB88A] mt-0.5" /> : <XCircle size={12} className="text-[#E67E7E] mt-0.5" />}
                <div><div className="text-[11px] text-[#F6F8FA]">{c.label}</div><div className="text-[10px] text-[#7C93A5] font-mono-cad">{c.note}</div></div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Phase VI stream flags</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
          {Object.entries(flags.data || {}).map(([k, v]) => (
            <div key={k} className="border-b cad-hairline pb-1 flex items-center gap-2">
              <div className="text-[11px] text-[#F6F8FA] flex-1 font-mono-cad">{k.replace("cadastre360_mp_", "")}</div>
              <button onClick={() => toggle.mutate({ name: k, enabled: !v })} className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm inline-flex items-center gap-1">
                {v ? <ToggleRight size={11} /> : <ToggleLeft size={11} />} {v ? "on" : "off"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const MineralPlus = () => {
  const [tab, setTab] = useState("resource_intel");
  return (
    <div className="p-6 space-y-4" data-testid="page-mineral-plus">
      <div className="flex items-start gap-3">
        <Gem size={32} className="text-[#C99A2E]" />
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">Cadastre360 · Phase VI · Sovereign Mineral Governance v1.6</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-0.5">Strategic Resource Intelligence & Future Mining Transformation</h1>
          <div className="text-[12.5px] text-[#B3C2CC] mt-1 max-w-4xl">Eighteen additive streams reusing existing enterprise services — no duplicate frameworks, zero regression.</div>
        </div>
      </div>
      <div className="cad-panel px-2 py-2 flex flex-wrap gap-1 sticky top-2 z-10" data-testid="mp-tabs">
        {[...STREAMS, ["acceptance", "Acceptance & Flags"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={"mp-tab-" + k}
                  className={"px-2.5 py-1.5 rounded-sm text-[10.5px] font-mono-cad uppercase tracking-[0.12em] inline-flex items-center gap-1.5 " +
                             (tab === k ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25")}>
            {k === "acceptance" ? <BadgeCheck size={11} className="text-[#C99A2E]" /> : <Gem size={11} className="text-[#C99A2E]" />} {l}
          </button>
        ))}
      </div>
      {tab === "acceptance" ? <AcceptancePanel /> : <StreamPanel k={tab} />}
    </div>
  );
};
